import { Component, Input, OnInit } from '@angular/core';
import { ColorService } from 'src/app/service/color/color.service';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';

interface ReadabilityRow {
  label: string;
  textColor: string;
  bgColor: string;
  ratio: number;
  normal: string;   // AAA / AA / Fail (normal body text)
  large: string;    // AAA / AA / Fail (large text)
  apca: number;     // APCA Lc, signed
  apcaLevel: string;
}

// Live readability preview embedded inside a blog post. Pick a color and see how it
// reads as text on white/black and how white/black text read on it, with the WCAG 2
// ratio and APCA (WCAG 3) Lc for each. Reuses the color tool's logic (ColorService)
// so the numbers match the /tools/color Readability section.
@Component( {
  selector: 'app-readability-preview',
  templateUrl: './readability-preview.component.html',
  styleUrls: [ './readability-preview.component.scss' ],
} )
export class ReadabilityPreviewComponent implements OnInit {

  @Input() hex = '#00BAFF';
  // When true, open on the bound [hex] (the post's color) instead of a random color.
  @Input() lockHex = false;

  text = '';
  rows: ReadabilityRow[] = [];

  // One engagement event on first interaction, not per keystroke.
  private tracked = false;

  constructor(
    private colorService: ColorService,
    private analyticsService: AnalyticsService,
  ) {
  }

  private trackUse(): void {
    if ( this.tracked ) {
      return;
    }
    this.tracked = true;
    this.analyticsService.logEvent( 'Blog Widget Used', { widget: 'readability-preview' } );
  }

  ngOnInit(): void {
    // Open on the bound [hex] when locked (the post's color); otherwise start from a
    // random named color each load so the widget feels alive. Either way the reader can
    // type any color to test.
    if ( !this.lockHex ) {
      const name = this.colorService.getRandomNamedColor();
      const code = this.colorService.getHexCode( name );
      if ( code ) {
        this.hex = '#' + code.toLowerCase();
      }
    }
    this.text = this.normalizeHex( this.hex );
    this.recompute();
  }

  onText( value: string ): void {
    const raw = ( value || '' ).replace( /[^0-9a-fA-F]/g, '' ).toLowerCase().slice( 0, 6 );
    this.text = '#' + raw;
    this.recompute();
    this.trackUse();
  }

  onPicker( value: string ): void {
    this.text = ( value || '' ).toLowerCase();
    this.recompute();
    this.trackUse();
  }

  // Block non-hex keystrokes outright (control keys and shortcuts pass through).
  onKey( event: KeyboardEvent ): void {
    if ( event.ctrlKey || event.metaKey ) {
      return;
    }
    if ( event.key && event.key.length === 1 && !/[0-9a-fA-F]/.test( event.key ) ) {
      event.preventDefault();
    }
  }

  get swatch(): string {
    return this.hex;
  }

  // Green for a pass at body size, amber for large only, red for fail.
  dotLevel( row: ReadabilityRow ): string {
    if ( row.normal === 'AAA' || row.normal === 'AA' ) {
      return 'pass';
    }
    return row.large === 'AAA' || row.large === 'AA' ? 'large' : 'fail';
  }

  private normalizeHex( value: string ): string {
    return '#' + ( value || '' ).trim().replace( /^#/, '' ).toLowerCase();
  }

  private recompute(): void {
    const v = ( this.text || '' ).replace( /[^0-9a-fA-F]/g, '' ).toLowerCase().slice( 0, 6 );
    if ( v.length !== 6 || !this.colorService.isValidHexCode( v ) ) {
      return;
    }
    this.hex = '#' + v;

    const white = '#ffffff';
    const black = '#000000';
    const mk = ( label: string, textColor: string, bgColor: string ): ReadabilityRow => {
      const ratio = this.colorService.contrastRatio( textColor, bgColor );
      const apca = this.colorService.apcaContrast( textColor, bgColor );
      return {
        label,
        textColor,
        bgColor,
        ratio,
        normal: this.colorService.wcagLevel( ratio ),
        large: this.colorService.wcagLevel( ratio, true ),
        apca,
        apcaLevel: this.colorService.apcaLevel( apca ),
      };
    };

    this.rows = [
      mk( 'On white', this.hex, white ),
      mk( 'White on', white, this.hex ),
      mk( 'On black', this.hex, black ),
      mk( 'Black on', black, this.hex ),
    ];
  }
}
